import { Component, DestroyRef, ElementRef, HostListener, Input, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, Subscription, catchError, switchMap, timer } from 'rxjs';
import { ManagementService } from '../../management.service';
import { UtilService } from '../../../../services/util.service';
import { BrandResearchStatus, ResearchRun, ResearchSnapshot } from '../../types';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { ResearchViewerComponent } from '../research-viewer/research-viewer.component';
import { MIN_VALID_FROM, defaultValidFrom, todayIso } from './research-dates';

// Poll only while a run is in flight and the drawer is open; research takes minutes, not seconds.
const POLL_INTERVAL_MS = 45_000;
// Transient poll failures re-arm the timer; after this many CONSECUTIVE failures we stop and say so
// (a dead chain that silently freezes the badge at "Researching…" is worse than admitting it).
const MAX_CONSECUTIVE_POLL_FAILURES = 4;
// A RUNNING row this old is presumed crashed; the DAG sweeps it to FAILED within its 3h window.
const STALE_RUNNING_MS = 2 * 60 * 60 * 1000;
// Approximate rendered height of the date popover — used to flip it upward when the Run button
// sits too close to the bottom of the drawer's scroll viewport.
const PICKER_HEIGHT_PX = 300;

const CHANNEL_LABELS: Record<string, string> = { meta: 'Meta', linkedin: 'LinkedIn' };

interface ChannelChip {
  text: string;   // compact, e.g. "Meta · US" or "LinkedIn · 6"
  title: string;  // full country list for the tooltip
}

/**
 * The brand drawer's entire research footprint: ONE row (status badge, per-channel chips, View
 * link, Run button — the date picker opens as a floating popover, not inline). Reading the MD
 * happens in ResearchViewerComponent, a dialog stacked over the drawer, so the already-long
 * drawer scroll doesn't grow (DEV-522). Purely informational: nothing here participates in the
 * brand form or its save payload. Trigger eligibility is the server's call — the button is only
 * disabled while a run is in flight, and a 400 is shown as-is.
 */
@Component({
  selector: 'app-research-section',
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './research-section.component.html',
  styleUrl: './research-section.component.scss',
})
export class ResearchSectionComponent implements OnInit {
  @Input({ required: true }) brandKey!: string;
  @Input() brandName: string | null = null;

  public readonly status = signal<BrandResearchStatus | null>(null);
  public readonly loadFailed = signal(false);
  public readonly pollLost = signal(false);
  public readonly triggering = signal(false);
  public readonly triggerError = signal<string | null>(null);
  public readonly pickerOpen = signal(false);
  public readonly pickerOpensUp = signal(false);
  public readonly validFromControl = new FormControl<string>(defaultValidFrom());
  public readonly minValidFrom = MIN_VALID_FROM;

  public readonly chips = computed<ChannelChip[]>(() => {
    const byChannel = new Map<string, Set<string>>();
    for (const snapshot of this.status()?.snapshots ?? []) {
      if (!byChannel.has(snapshot.channel)) byChannel.set(snapshot.channel, new Set());
      byChannel.get(snapshot.channel)!.add(snapshot.country);
    }
    return [...byChannel.entries()].map(([channel, countrySet]) => {
      const label = CHANNEL_LABELS[channel] ?? channel.charAt(0).toUpperCase() + channel.slice(1);
      const countries = [...countrySet].sort();
      // Compact text keeps the one-line row from outgrowing the drawer; the tooltip carries the rest.
      const text = countries.length <= 2 ? countries.join(', ') : `${countries.length}`;
      const title = countries.length <= 2 ? countries.join(', ') : `${countries.length} countries: ${countries.join(', ')}`;
      return { text: `${label} · ${text}`, title };
    });
  });

  public readonly badge = computed<{ kind: string; text: string; title?: string }>(() => {
    const status = this.status();
    if (!status) return { kind: 'muted', text: 'Loading…' };
    const run = status.latestRun;
    if (run?.status === 'PENDING') {
      return { kind: 'running', text: 'Queued', title: `Requested ${this.formatRelative(run.createdAt)} — picked up within ~10 minutes` };
    }
    if (run?.status === 'RUNNING') {
      return this.isStale(run)
        ? { kind: 'muted', text: 'Stale run', title: 'This run looks stuck; it will be failed automatically. You can re-run once it is.' }
        : { kind: 'running', text: 'Researching…', title: `Started ${this.formatRelative(run.startedAt ?? run.createdAt)}` };
    }
    if (run?.status === 'FAILED') {
      return { kind: 'failed', text: 'Failed', title: run.error ?? 'Research failed' };
    }
    const researchedAt = this.latestResearchedAt();
    if (researchedAt) return { kind: 'ok', text: `Researched · ${this.formatRelative(researchedAt)}`, title: new Date(researchedAt).toLocaleString() };
    return { kind: 'muted', text: 'Never researched' };
  });

  public readonly runActive = computed<boolean>(() => {
    const run = this.status()?.latestRun ?? null;
    return !!run && (run.status === 'PENDING' || (run.status === 'RUNNING' && !this.isStale(run)));
  });

  public readonly hasResearch = computed<boolean>(() => (this.status()?.snapshots.length ?? 0) > 0);

  private readonly api = inject(ManagementService);
  private readonly dialog = inject(MatDialog);
  private readonly utilService = inject(UtilService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);
  // The drawer itself is a MatDialog; its ref lets us block backdrop-dismiss while a trigger
  // request is in flight, so an error outcome is never lost to an accidental outside click.
  private readonly drawerRef = inject<MatDialogRef<unknown> | null>(MatDialogRef, { optional: true });
  private pollSubscription: Subscription | null = null;
  private consecutivePollFailures = 0;

  // All status loads flow through ONE switchMap pipeline: a newer request cancels an older
  // in-flight one, so a stale response can never overwrite fresher state (the poll-vs-trigger
  // race: a pre-trigger GET resolving after the post-trigger GET would show "Never researched"
  // and re-enable the Run button).
  private readonly reload$ = new Subject<void>();

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => this.api.getBrandResearch(this.brandKey).pipe(
          catchError(() => {
            this.onLoadError();
            return EMPTY;
          })
        )),
        takeUntilDestroyed()
      )
      .subscribe(status => this.onLoadSuccess(status));
  }

  ngOnInit(): void {
    this.load();
  }

  // Close the date popover on any click outside this section (the drawer stays open). A target
  // detached by the same click (e.g. Cancel removed the popover subtree via *ngIf before the
  // event reached document) is ignored — containment can't be judged against the current DOM.
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target || !target.isConnected) return;
    if (this.pickerOpen() && !this.triggering() && !this.host.nativeElement.contains(target)) {
      this.closePicker();
    }
  }

  // Scoped to the popover subtree (bound in the template): closes only the popover and stops the
  // event before Material's overlay sees it, so Escape doesn't also dismiss the whole drawer.
  onPickerEscape(event: Event): void {
    event.stopPropagation();
    this.closePicker();
  }

  public get maxValidFrom(): string {
    return todayIso();
  }

  public get canConfirm(): boolean {
    const value = this.validFromControl.value;
    return !this.triggering() && !!value && value >= MIN_VALID_FROM && value <= this.maxValidFrom;
  }

  // The opening click never reaches the document handler: the template stops propagation at the
  // .run-anchor level (app-button's (clicked) output carries no event object to stop here).
  public openPicker(): void {
    if (this.runActive()) return;
    this.validFromControl.setValue(defaultValidFrom());
    this.triggerError.set(null);
    // Flip upward when there isn't room below within the drawer's SCROLL CONTAINER — an
    // overflow:auto ancestor clips absolutely-positioned descendants, so the viewport alone is
    // the wrong boundary (the drawer footer sits below the scroll area).
    const anchor = (this.host.nativeElement as HTMLElement).querySelector('.run-anchor');
    const anchorBottom = anchor?.getBoundingClientRect().bottom ?? 0;
    const container = (this.host.nativeElement as HTMLElement).closest('.drawer-body');
    const containerBottom = container?.getBoundingClientRect().bottom ?? window.innerHeight;
    this.pickerOpensUp.set(anchorBottom + PICKER_HEIGHT_PX > Math.min(containerBottom, window.innerHeight));
    this.pickerOpen.set(true);
  }

  public closePicker(): void {
    this.pickerOpen.set(false);
  }

  public run(): void {
    if (!this.canConfirm) return;
    this.triggering.set(true);
    this.triggerError.set(null);
    // Stop any pending poll: its response would race the trigger's own refresh.
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    // Capture-and-restore rather than blanket true/false: DrawerFormBase's effect owns this flag
    // for saves, and stomping it to false could unlock the drawer mid-save.
    const previousDisableClose = this.drawerRef?.disableClose ?? false;
    if (this.drawerRef) this.drawerRef.disableClose = true;
    const restoreDrawerClose = () => {
      if (this.drawerRef) this.drawerRef.disableClose = previousDisableClose;
    };
    this.api.triggerBrandResearch(this.brandKey, this.validFromControl.value || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          restoreDrawerClose();
          this.triggering.set(false);
          this.pickerOpen.set(false);
          // Snackbar so the outcome survives even if the drawer is closed right after.
          this.utilService.openSnackBar('Research queued — it will be picked up within ~10 minutes', { variant: 'success', position: 'center', duration: 4000 });
          this.load(); // reflect the PENDING row immediately and start polling
        },
        error: error => {
          restoreDrawerClose();
          this.triggering.set(false);
          // 409 = someone else just triggered; refresh so the badge tells that story.
          if (error?.status === 409) this.load();
          // 4xx messages are intentional and actionable; anything else gets a generic line.
          const serverMessage = error?.status >= 400 && error?.status < 500 ? error?.error?.message : null;
          this.triggerError.set(serverMessage ?? 'Failed to queue research — please try again.');
        },
      });
  }

  public openViewer(): void {
    if (!this.hasResearch()) return;
    this.dialog.open(ResearchViewerComponent, {
      width: '760px',
      maxWidth: '90vw',
      autoFocus: false,
      data: {
        brandKey: this.brandKey,
        brandName: this.brandName ?? this.brandKey,
        snapshots: this.status()?.snapshots ?? [],
      },
    });
  }

  private load(): void {
    this.reload$.next();
  }

  private onLoadSuccess(status: BrandResearchStatus): void {
    this.consecutivePollFailures = 0;
    this.pollLost.set(false);
    this.loadFailed.set(false);
    this.status.set(status);
    this.schedulePollIfActive();
  }

  private onLoadError(): void {
    this.consecutivePollFailures += 1;
    this.loadFailed.set(this.status() === null); // keep showing data we already have
    // A single transient failure must not kill the poll chain mid-research — re-arm and retry.
    // After repeated failures, stop and SAY so instead of freezing at "Researching…" forever.
    if (this.status() !== null && this.runActive()) {
      if (this.consecutivePollFailures < MAX_CONSECUTIVE_POLL_FAILURES) {
        this.schedulePollIfActive();
      } else {
        this.pollLost.set(true);
      }
    }
  }

  private schedulePollIfActive(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    if (!this.runActive()) return;
    this.pollSubscription = timer(POLL_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }

  private isStale(run: ResearchRun): boolean {
    const reference = run.startedAt ?? run.createdAt;
    return !!reference && Date.now() - new Date(reference).getTime() > STALE_RUNNING_MS;
  }

  private latestResearchedAt(): string | null {
    const dates = (this.status()?.snapshots ?? [])
      .map((snapshot: ResearchSnapshot) => snapshot.researchedAt)
      .filter((date): date is string => !!date);
    return dates.length ? dates.sort().at(-1)! : null;
  }

  private formatRelative(iso: string | null): string {
    if (!iso) return 'recently';
    const timestamp = new Date(iso).getTime();
    if (!Number.isFinite(timestamp)) return 'recently';
    const minutes = Math.round((Date.now() - timestamp) / 60_000);
    if (minutes < 1) return 'just now'; // includes small negative skew
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
  }
}
